import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { TitleCasePipe } from '@angular/common';
import { INCOME_SOURCES } from "src/app/constants/contact";
import { DBL_CLICK_TIMEOUT } from "src/app/constants/edit";
import { Income } from "src/app/models/income.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { clean } from "src/app/utils/numeric";
import { formatCurrency } from "src/app/utils/format";
import { incomeIcon } from "src/app/utils/icons";

@Component({
    selector: 'incomes-compact',
    templateUrl: './incomes-compact.component.html',
    styleUrls: ['./incomes-compact.component.scss'],
    providers: [TitleCasePipe],
    standalone: false
})

export class IncomesCompactComponent implements OnInit, OnChanges {

    /** */
    @Input() contactId: number | null = null

    /** */
    @Input() language: string = 'EN'

    /** */
    @Input() words = {}

    /** */
    @Output() modified: EventEmitter<Object> = new EventEmitter()

    /** A list of incomes associated with the current contact. */
    protected incomes: Income[] = []

    /** A form editor for incomes. */
    protected incomeEdit: FormGroup | null = null

    /** Available communication types. */
    public incomeSources: string[] = INCOME_SOURCES

    /** A debounce timer for click events. Used to wait for the user to click twice before performing an action. */
    private dblClickWatcher: ReturnType<typeof setTimeout> = null

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private titleCasePipe: TitleCasePipe) {

        this.incomeEdit = this.fb.group({
            id: new FormControl<number>(0, [Validators.required]),
            contactId: new FormControl<number>(0, [Validators.required]),
            type: new FormControl<string | null>(null, [Validators.required]),
            value: new FormControl<string | null>(null),
            primary: new FormControl<boolean>(false)
        })

    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.contactId) {
            this.fetch()
        }
    }
    /**
     * 
     */
    private fetch = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}/incomes`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.incomes) {
                return
            }
            
            this.incomes = res.data.incomes
        })
    }

    /**
     * Given an income type incomeity return a material icon to display.
     * @param type An income type to map to an icon.
     */
    protected incomeIcon = (type: string): string => {
        return incomeIcon(type)
    }

    /**
     * 
     * @param e 
     */
    protected addIncome = (e: Event): void => {
        this.cancelIncome(e)

        if (!this.incomes) {
            this.incomes = []
        }

        const income = new Income()
        this.incomeEdit.patchValue({
            id: 0,
            contactId: this.contactId,
            type: 'wages',
            value: formatCurrency(0),
            primary: (this.incomes.length <= 0)
        })

        this.incomes.push({
            ...income,
            id: 0,
            primary: (this.incomes.length <= 0),
            editing: true
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected editIncome = (e: Event, index: number): void => {
        this.cancelIncome(e)
        
        if (!this.incomes || (this.incomes.length <= 0)) {
            return
        }
        
        if (this.dblClickWatcher !== null) {
            const income = this.incomes[index]
            this.incomeEdit.patchValue({
                ...income,
                value: formatCurrency(income.value)
            }, {emitEvent: false})

            income.editing = true
        }

        const ctx = this
        this.dblClickWatcher = setTimeout(() => {
            ctx.dblClickWatcher = null
        }, DBL_CLICK_TIMEOUT)
    }

    /**
     * 
     * @param e 
     */
    protected cancelIncome = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        this.incomeEdit.reset()
        const index = this.incomes.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const income = this.incomes[index]
        if (income.id && (income.id > 0)) {
            income.editing = false
            return
        }

        this.incomes.splice(index, 1)
    }

    /**
     * 
     * @param e 
     */
    protected saveIncome = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const index = this.incomes.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const changes = this.incomeEdit.value
        changes.value = clean(changes.value)

        const income = Object.assign(this.incomes[index], changes)

        if (income.id && (income.id > 0)) {
            this.modifyIncome(income)
            return
        }

        this.insertIncome(income)
    }

    /**
     * 
     * @param income 
     */
    private insertIncome = (income: Income) => {
        if (!income) {
            return
        }

        const payload = {
            incomes: [
                JSON.parse(JSON.stringify(income))
            ]
        }

        delete(payload.incomes[0].id)

        this.master.post(`contacts/${this.contactId}/incomes`, payload, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            income.editing = false
            this.incomeEdit.reset()

            if (!res.data || !res.data.incomes || (res.data.incomes.length <= 0)) {
                return
            }
            
            income.id = parseInt(res.data.incomes[0].id)
            this.modified.emit({incomes: [{id: income.id}]})
        })
    }

    /**
     * 
     * @param income 
     */
    private modifyIncome = (income: Income) => {
        if (!income || !income.id) {
            return
        }

        const payload = JSON.parse(JSON.stringify(income))

        delete(payload.id)

        this.master.put(`contacts/${this.contactId}/incomes/${income.id}`, {income: payload}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            income.editing = false
            this.incomeEdit.reset()
            this.modified.emit({incomes: [{id: income.id}]})
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected discardIncome = (e: Event, index: number): void => {
        if (!this.incomes || (this.incomes.length <= 0) || (index < 0) || (index >= this.incomes.length)) {
            return
        }

        const income = this.incomes[index]
        const incomeId = income.id

        this.master.discard(`contacts/${this.contactId}/incomes/${incomeId}`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            this.incomes.splice(index, 1)
            this.modified.emit({incomes: [{id: income.id}]})
        })
    }
}